import React from 'react';
import { FormattedMessage, defineMessages, type MessageDescriptor } from 'react-intl';

import wordIcon from '../../img/word.svg';
import googleDocsIcon from '../../img/googleDocs.svg';
import OneDriveIcon from '../../img/oneDriveLogo.svg';
import type { SourcesItemPropsData } from '../SourcesListCard/SourcesItem.tsx';
import type { ImportStrategyComponentType } from '../types.ts';

// oxlint-disable-next-line import/no-cycle
import { ImportFromLocal } from './ImportFromLocal/ImportFromLocal.tsx';
// oxlint-disable-next-line import/no-cycle
import { ImportFromGD } from './ImportFromGD/ImportFromGD.tsx';
// oxlint-disable-next-line import/no-cycle
import { ImportFromOneDrive } from './ImportFromOneDrive/ImportFromOneDrive.tsx';

export type ImportStrategy = {
	key: string;
	folderName: MessageDescriptor;
	component: ImportStrategyComponentType;
	card: SourcesItemPropsData;
};

// These keys also are used for analytics
export enum ActiveImports {
	NONE = '',
	IMPORT_FROM_LOCAL = 'wordDoc',
	IMPORT_FROM_GD = 'gDoc',
	IMPORT_FROM_ONE_DRIVE = 'office365Doc',
}

const i18n = defineMessages({
	oneDriveSourcesListCardDataName: {
		id: 'import-from-sources.sources-list.oneDriveSourcesListCardDataName',
		defaultMessage: 'OneDrive',
		description: 'Icon title for uploading document from Microsoft OneDrive',
	},
	msWordSourcesCardDataName: {
		id: 'import-from-sources.sources-list.msWordCardDataName',
		defaultMessage: 'Microsoft Word',
		description: 'Icon title for uploading Microsoft Word document',
	},
	gdocSourcesCardDataName: {
		id: 'import-from-sources.sources-list.gdocCardDataName',
		defaultMessage: 'Google Docs',
		description: 'Icon title for uploading document from Google Drive',
	},
	oneDriveFolderName: {
		id: 'import-from-sources.sources-list.oneDriveFolderName',
		defaultMessage: 'OneDrive',
		description:
			'Default name for the folder in the Confluence page tree where documents imported from Microsoft OneDrive are placed. Shown as a page tree node label after an OneDrive import completes.',
	},
	msWordFolderName: {
		id: 'import-from-sources.sources-list.msWordFolderName',
		defaultMessage: 'Word Documents',
		description:
			'Default name for the folder in the Confluence page tree where documents imported from Microsoft Word are placed. Shown as a page tree node label after a Word document import completes.',
	},
	gdocFolderName: {
		id: 'import-from-sources.sources-list.gdocFolderName',
		defaultMessage: 'Google Drive',
		description:
			'Default name for the folder in the Confluence page tree where documents imported from Google Docs are placed. Shown as a page tree node label after a Google Docs import completes.',
	},
});

export const IMPORT_FOLDER_NAMES: {
	readonly wordDoc: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly gDoc: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly office365Doc: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[ActiveImports.IMPORT_FROM_LOCAL]: i18n.msWordFolderName,
	[ActiveImports.IMPORT_FROM_GD]: i18n.gdocFolderName,
	[ActiveImports.IMPORT_FROM_ONE_DRIVE]: i18n.oneDriveFolderName,
} as const;

export const IMPORT_STRATEGIES: ImportStrategy[] = [
	{
		key: ActiveImports.IMPORT_FROM_LOCAL,
		folderName: IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_LOCAL],
		component: ImportFromLocal,
		card: {
			name: <FormattedMessage {...i18n.msWordSourcesCardDataName} />,
			iconURL: wordIcon,
		},
	},
	{
		key: ActiveImports.IMPORT_FROM_GD,
		folderName: IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_GD],
		component: ImportFromGD,
		card: {
			name: <FormattedMessage {...i18n.gdocSourcesCardDataName} />,
			iconURL: googleDocsIcon,
		},
	},
	{
		key: ActiveImports.IMPORT_FROM_ONE_DRIVE,
		folderName: IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_ONE_DRIVE],
		component: ImportFromOneDrive,
		card: {
			name: <FormattedMessage {...i18n.oneDriveSourcesListCardDataName} />,
			iconURL: OneDriveIcon,
		},
	},
];
